/*
 * Search fallback for colors that live only in the product HANDLE and/or the
 * custom.color_label metafield (e.g. "obsidian", "coconut"). Shopify's native
 * search indexes neither handles nor metafields, so those queries return nothing.
 *
 * This augments BOTH the /search results page (#product-grid) and the predictive
 * search dropdown (<predictive-search>):
 *  - MATCH against a JSON index that includes handle + title + color_label +
 *    tags + vendor + type (/collections/all?view=searchindex, see
 *    templates/collection.searchindex.liquid — Liquid CAN read the metafield);
 *  - DISPLAY each extra match as the real product card (fetched from
 *    /products/<handle>?view=card, templates/product.card.liquid) so injected
 *    cards look like native ones — correct B2B price, color label and styling.
 *    If a card fetch fails it falls back to a minimal title tile.
 */
(function () {
  'use strict';

  /* ---- helpers ---- */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }
  function sized(src, w) {
    if (!src) return src;
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function tokenize(q) { return (q || '').toLowerCase().split(/\s+/).filter(Boolean); }
  function imageOf(p) {
    return p.image || (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
  }

  // Catalog search index (handle + title + color_label metafield + tags), fetched
  // once. Uses /collections/all?view=searchindex (templates/collection.searchindex
  // .liquid, 50/page) rather than /products.json, because products.json can't
  // expose metafields — and colors like "coconut" live only in custom.color_label.
  var productsPromise = null;
  function getProducts() {
    if (productsPromise) return productsPromise;
    productsPromise = (function () {
      var acc = [];
      function page(p) {
        return fetch('/collections/all?view=searchindex&page=' + p)
          .then(function (r) { return r.ok ? r.text() : '[]'; })
          .then(function (t) {
            var arr = [];
            try { arr = JSON.parse(t) || []; } catch (e) { arr = []; }
            acc = acc.concat(arr);
            // Keep paging until a page comes back empty. The page size is NOT
            // fixed (the store returns ~20/page even though the template requests
            // `paginate by: 50`), so don't gate on an exact count — that stopped
            // after page 1 and left most of the catalog (incl. most colors)
            // unindexed. Safety cap at 50 pages.
            if (arr.length > 0 && p < 50) return page(p + 1);
            return acc;
          });
      }
      return page(1);
    })().catch(function () { return []; });
    return productsPromise;
  }

  // Real card HTML per handle (correct B2B price/labels), cached.
  var cardCache = {};
  function getCard(handle) {
    if (cardCache[handle]) return cardCache[handle];
    cardCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '?view=card')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (t) {
        t = (t || '').trim();
        // If the ?view=card alternate template didn't apply (e.g. a full page or
        // an access/lock page came back), treat it as a miss so simpleCard is used
        // instead of injecting a whole HTML document as a "card".
        if (/<!doctype|<html[\s>]/i.test(t)) return '';
        return t;
      })
      .catch(function () { return ''; });
    return cardCache[handle];
  }

  function hayFor(p) {
    return (
      p.handle + ' ' + (p.title || '') + ' ' + (p.color || '') + ' ' +
      ((p.tags || []).join(' ')) + ' ' +
      (p.vendor || '') + ' ' + (p.type || p.product_type || '')
    ).toLowerCase();
  }
  function matchesTokens(p, tokens) {
    var hay = hayFor(p);
    for (var t = 0; t < tokens.length; t++) {
      if (hay.indexOf(tokens[t]) === -1) return false;
    }
    return true;
  }
  function matchProducts(products, q, existing, limit) {
    var tokens = tokenize(q);
    if (!tokens.length) return [];
    var out = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (existing[p.handle]) continue;
      if (matchesTokens(p, tokens)) { out.push(p); if (limit && out.length >= limit) break; }
    }
    return out;
  }
  function handleFromHref(href) {
    var m = (href || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }
  function handlesIn(root) {
    var existing = {};
    root.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var h = handleFromHref(a.getAttribute('href'));
      if (h) existing[h] = true;
    });
    return existing;
  }
  function now() {
    return (window.performance && performance.now) ? performance.now() : Date.now();
  }

  // Loading spinner shown in `parent` while the fallback fetches run. Returns a
  // `finish(cb)` function: it keeps the spinner visible for a minimum time (so it
  // doesn't flash invisibly when the catalog is already cached), then removes it
  // and runs cb. When `active` is false (or no parent) it's a no-op that just
  // runs cb immediately.
  var SPINNER_MIN_MS = 350;
  function makeSpinner(parent, modifier, active) {
    if (!active || !parent || parent.querySelector('.akog-search-spinner')) {
      return function (cb) { if (cb) cb(); };
    }
    var el = document.createElement('div');
    el.className = 'akog-search-spinner' + (modifier ? ' ' + modifier : '');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-label', 'Loading');
    // The spinning circle is a child element (NOT a ::after pseudo) so the spinner
    // isn't an empty <div> — the theme's global `div:empty { display:none }` rule
    // would otherwise hide it.
    el.appendChild(document.createElement('span'));
    parent.appendChild(el);
    var startedAt = now();
    return function (cb) {
      var wait = Math.max(0, SPINNER_MIN_MS - (now() - startedAt));
      setTimeout(function () {
        if (el && el.parentNode) el.parentNode.removeChild(el);
        if (cb) cb();
      }, wait);
    };
  }

  // Minimal fallback tile if the real card fetch fails.
  function simpleCard(p) {
    var raw = imageOf(p);
    // Index images are already requested at width 600; use as-is (don't double-size).
    var media = raw
      ? '<span class="akog-search-extra__media"><img src="' + esc(raw) + '" alt="' + esc(p.title) + '" loading="lazy"></span>'
      : '<span class="akog-search-extra__media"></span>';
    return '<a class="akog-search-extra" href="/products/' + esc(p.handle) + '">' +
      media + '<span class="akog-search-extra__title">' + esc(p.title) + '</span></a>';
  }

  /* ---- 1. /search results page ---- */
  // Build (or find) the results <ul>. On a zero-result search the page renders the
  // empty-state #product-grid WITHOUT a ul.product-grid, so we create one (and
  // clear the "no results" message) — otherwise color-only matches never appear.
  function ensureGrid(container) {
    var grid = container.querySelector('ul.product-grid');
    if (grid) return grid;
    container.classList.remove('collection--empty');
    var msg = container.querySelector('.title-wrapper');
    if (msg) msg.parentNode.removeChild(msg);
    var overlay = container.querySelector('.loading-overlay');
    if (overlay) overlay.parentNode.removeChild(overlay);
    grid = document.createElement('ul');
    grid.className = 'grid product-grid template-search__grid ' +
      'grid--2-col-tablet-down grid--3-col-tablet grid--4-col-desktop';
    grid.setAttribute('role', 'list');
    container.appendChild(grid);
    return grid;
  }

  function augmentResultsPage() {
    var container = document.getElementById('product-grid');
    if (!container) return;

    // The results grid is cloaked until we're done (see main-search.liquid), so
    // native noise never flickers before we prune/add. Reveal at every exit path.
    function reveal() { document.body.classList.add('akog-search-ready'); }

    var q = (new URLSearchParams(window.location.search).get('q') || '').trim();
    var tokens = tokenize(q);
    if (!tokens.length) { reveal(); return; }

    // Show a spinner while cloaked (the grid is hidden until reveal()).
    var finishSpinner = makeSpinner(container, null, true);

    function updateCount() {
      var g = container.querySelector('ul.product-grid');
      var total = g ? g.querySelectorAll(':scope > li').length : 0;
      var label = total + ' result' + (total === 1 ? '' : 's') + ' for “' + q + '”';
      var tpl = document.querySelector('.template-search');
      if (tpl) tpl.classList.remove('template-search--empty');
      // Cover both layouts: the header status (no-facet) and the facet sidebar
      // count (#ProductCountDesktop / #ProductCount / .product-count-vertical).
      ['.template-search__header [role="status"]', '#ProductCountDesktop', '#ProductCount', '.product-count-vertical']
        .forEach(function (sel) { var el = document.querySelector(sel); if (el) el.textContent = label; });
    }
    // facets.js can re-render the sidebar count back to the native total AFTER we
    // set it, so re-apply a few times to win.
    function updateCountPersistent() {
      updateCount();
      window.setTimeout(updateCount, 500);
      window.setTimeout(updateCount, 1500);
    }

    getProducts().then(function (products) {
      // Products matching ALL query tokens — the same precise matching the
      // predictive dropdown uses.
      var matchedList = [];
      for (var i = 0; i < products.length; i++) {
        if (matchesTokens(products[i], tokens)) matchedList.push(products[i]);
      }
      // Nothing we can identify from the index — leave native results untouched
      // (protects queries only native understands, e.g. native synonyms).
      if (!matchedList.length) { finishSpinner(reveal); return; }

      // Authoritative: render EXACTLY the matched set (like the predictive
      // dropdown), replacing native's loose/paginated results entirely. This
      // removes native noise AND can't be re-polluted by the infinite-scroll app
      // (pagination is also disabled server-side). Cap the card-fetch count.
      var picks = matchedList.slice(0, 60);
      Promise.all(picks.map(function (p) { return getCard(p.handle); })).then(function (cards) {
        finishSpinner(function () {
          var g = ensureGrid(container);
          while (g.firstChild) g.removeChild(g.firstChild);
          var html = picks.map(function (p, i) {
            return '<li class="grid__item">' + (cards[i] || simpleCard(p)) + '</li>';
          }).join('');
          g.insertAdjacentHTML('beforeend', html);
          // The injected cards' "Add to Pre-selection" buttons are added after
          // page load, so preselection.js's initial state sync already ran. Fire
          // its change event to re-sync, so already-selected products correctly
          // show "Remove from Pre-selection".
          document.dispatchEvent(new CustomEvent('akog:preselection:change'));
          updateCountPersistent();
          reveal();
        });
      });
    }).catch(function () { finishSpinner(reveal); });
  }

  // Re-sync the "Add to Pre-selection" buttons. preselection.js only syncs button
  // state on DOMContentLoaded, so cards rendered after load (predictive dropdown +
  // our injected results) keep showing "Add" even when already selected. Firing
  // its change event re-runs syncAllButtonStates over every button now in the DOM.
  function syncPreselection() {
    document.dispatchEvent(new CustomEvent('akog:preselection:change'));
  }
  var _syncTimer = null;
  function syncPreselectionSoon() {
    if (_syncTimer) clearTimeout(_syncTimer);
    _syncTimer = setTimeout(syncPreselection, 150);
  }

  /* ---- 2. predictive search dropdown ---- */
  function augmentPredictive(host) {
    var q = (host.getQuery ? host.getQuery() : '').trim();
    if (!tokenize(q).length) return;
    var root = host.querySelector('[data-predictive-search]');
    if (!root) return;

    // Idempotent: bail if our tiles are already present for this render, or a run
    // is already in flight for this query — so the patch + MutationObserver double
    // trigger can't loop or inject twice. A native re-render clears innerHTML
    // (removing .akog-pred-extra), which lets us inject again for the next query.
    if (root.querySelector('.akog-pred-extra')) return;
    if (root.__akogBusy === q) return;
    root.__akogBusy = q;
    function done() { if (root.__akogBusy === q) root.__akogBusy = false; }

    // Spinner only when there are no native product results in this render.
    var hasNative = !!root.querySelector('#predictive-search-results-products-list');
    var spinnerHost = root.querySelector('#predictive-search-results') || root;
    var finishSpinner = makeSpinner(spinnerHost, 'akog-search-spinner--predictive', !hasNative);

    getProducts().then(function (products) {
      if ((host.getQuery ? host.getQuery() : '').trim() !== q) { done(); finishSpinner(); return; }
      var existing = handlesIn(root);
      var matches = matchProducts(products, q, existing, 12);
      if (!matches.length) { done(); finishSpinner(); return; }

      return Promise.all(matches.map(function (p) { return getCard(p.handle); }))
        .then(function (cards) {
          finishSpinner(function () {
          done();
          if ((host.getQuery ? host.getQuery() : '').trim() !== q) return;
          if (root.querySelector('.akog-pred-extra')) return;
          // re-read in case the native render finished in the meantime
          var here = handlesIn(root);
          var tiles = '';
          cards.forEach(function (card, i) {
            var p = matches[i];
            if (here[p.handle]) return;
            var inner = card || simpleCard(p);
            tiles += '<li id="predictive-search-option-akog-' + i + '" ' +
              'class="akog-pred-extra predictive-search__list-item predictive-search__tile grid__item" ' +
              'role="option" aria-selected="false">' + inner + '</li>';
          });
          if (!tiles) return;

          var list = root.querySelector('#predictive-search-results-products-list');
          if (list) {
            list.insertAdjacentHTML('beforeend', tiles);
          } else {
            var group =
              '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper">' +
              '<div class="predictive-search__result-group"><div>' +
              '<ul id="predictive-search-results-products-list" ' +
              'class="predictive-search__results-list predictive-search__results-grid list-unstyled grid product-grid grid--2-col-tablet-down grid--3-col-tablet grid--6-col-desktop" ' +
              'role="group">' + tiles + '</ul></div></div></div>';
            var listbox = root.querySelector('#predictive-search-results');
            (listbox || root).insertAdjacentHTML('afterbegin', group);
          }
          syncPreselection();
          });
        });
    }).catch(function () { done(); finishSpinner(); });
  }

  // Robust trigger: watch each predictive-search element's results region and run
  // the fallback whenever it changes. This works even if the renderSearchResults
  // prototype patch doesn't apply (e.g. a differently-built predictive-search.js
  // on the deployed theme). augmentPredictive is idempotent, so the observer and
  // the patch can both fire without duplicating tiles.
  function observePredictive(host) {
    if (host.__akogObserved) return;
    var root = host.querySelector('[data-predictive-search]');
    if (!root) return;
    host.__akogObserved = true;
    new MutationObserver(function () {
      try { augmentPredictive(host); } catch (e) { /* fallback only */ }
      // Native predictive cards are also rendered after load — re-sync their
      // pre-selection buttons (debounced so a render burst fires it once).
      syncPreselectionSoon();
    }).observe(root, { childList: true, subtree: true });
  }

  function patchPredictive() {
    var ctor = window.customElements && customElements.get('predictive-search');
    if (!ctor || !ctor.prototype || ctor.prototype.__akogPatched) return;
    var proto = ctor.prototype;
    var orig = proto.renderSearchResults;
    if (typeof orig !== 'function') return;
    proto.renderSearchResults = function (markup) {
      orig.call(this, markup);
      try { augmentPredictive(this); } catch (e) { /* fallback only */ }
    };
    proto.__akogPatched = true;
  }

  /* ---- boot ---- */
  function boot() {
    augmentResultsPage();
    if (window.customElements) {
      if (customElements.get('predictive-search')) patchPredictive();
      else customElements.whenDefined('predictive-search').then(patchPredictive);
    }
    // Also observe the predictive elements directly, so the dropdown fallback
    // fires even when the prototype patch doesn't take effect.
    document.querySelectorAll('predictive-search').forEach(function (el) {
      try { observePredictive(el); } catch (e) { /* fallback only */ }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
